import { z } from "zod";
import { AI_PAGE_GROUP_ROLES, AI_PAGE_STATUSES, AI_PAGE_TIERS } from "@goppy/domain";

/**
 * DTO `GET /v1/admin/ai/registry` — admin ĐỌC Sổ năng lực trợ lý v2 (US-AIV2-0071 · @UC0150).
 *
 * 🔴 **Sổ là NGUỒN, DTO này là CHIẾU.** Enum `tier`/`status`/`roles` lấy thẳng từ
 * `packages/domain/src/ai/registry/schema.ts` — KHÔNG khai lại ba whitelist ở đây. Repo đã có tiền lệ
 * hai whitelist trôi khỏi nhau (`TX_TYPES` domain ↔ CHECK migration, CLAUDE.md §3.8); một Sổ mà có
 * hai bảng trạng thái thì câu "live chỉ đi lên" mất nghĩa ở đúng chỗ người ta đọc nó.
 *
 * **Hình chiếu = đúng các cột của `docs/generated/ai-registry.md`** (QĐ-1 của task): đó là bảng PO
 * đọc, và AC2 đòi màn admin hiện *cùng* con số. Chọn cùng hình chiếu làm cho "cùng con số" trở thành
 * một phép so máy chạy được (`admin-ai-registry.doc-parity.invariants.spec.ts`), không phải một lời hứa.
 *
 * 🔴 **KHÔNG mang chữ của prompt** (QĐ-2): `llm.description`, `llm.examples` nguyên văn, `templates`,
 * `labels`, `facts` đều KHÔNG ra khỏi máy chủ — chỉ `slotCount`/`exampleCount`. Hệ quả có chủ đích:
 * payload không mang secret · PII · tiền, nên không có field nào phải che theo vai admin, và GET mở
 * cho cả 4 vai đúng tiền lệ `admin-ai-config` GET. Muốn đọc câu ví dụ thì đọc Sổ trong git — nó là code.
 *
 * KHÔNG có schema request/body: Sổ sửa qua PR, không qua admin (index.md §1 "ngoài phạm vi").
 */

/** Một dòng của bảng Sổ — đúng các cột `ai-registry.md` in ra, cộng `flags` (admin sở hữu feature flag). */
export const adminAiRegistryPageSchema = z
  .object({
    id: z.string(),
    uc: z.string(),
    tier: z.enum(AI_PAGE_TIERS),
    status: z.enum(AI_PAGE_STATUSES),
    /** Vai NHÓM mà trang mở cho (`owner`…) — KHÔNG phải vai admin; hai actor khác nhau (CLAUDE.md §1). */
    roles: z.array(z.enum(AI_PAGE_GROUP_ROLES)),
    /** Cờ tính năng phải BẬT để trang sống — khoá `feature_flags`, nối được sang màn Feature Flags. */
    flags: z.array(z.string()),
    /** SỐ ĐẾM ô, không phải câu hỏi của ô (QĐ-2). */
    slotCount: z.number().int().nonnegative(),
    /** SỐ ĐẾM câu ví dụ, không phải câu ví dụ (QĐ-2). */
    exampleCount: z.number().int().nonnegative(),
    /** Cửa ghi/đọc (`funds.createFund`) — `null` với tier `redirect`, trang đó chỉ mở màn hình. */
    executor: z.string().nullable(),
    /** Route mobile mở ra (`/groups/:groupId`) — `null` với mọi tier khác `redirect`. */
    screen: z.string().nullable(),
    /**
     * Ma trận dẫn đường theo KẾT QUẢ (US-AIV2-tourguide-0005 · @UC0364): mỗi kết quả của trang (`journey.outcomes` của Sổ) ⇒ trang gợi ý
     * đi tiếp (`next`, có thể rỗng — "không mời gì") + thẻ dẫn đường mà câu của kết quả đã nói (`covers`). Trang `redirect`: `[]` (chỉ mở
     * màn hình — không bước tiếp theo nào chạy). Thay `journeyAfter` (một danh sách cho MỌI nhánh — không còn ở Sổ trang đọc/ghi).
     */
    journeyOutcomes: z.array(
      z
        .object({
          outcome: z.string(),
          next: z.array(z.string()),
          covers: z.array(z.string()),
        })
        .strict(),
    ),
  })
  .strict();

export type AdminAiRegistryPage = z.infer<typeof adminAiRegistryPageSchema>;

/** Đếm trang theo trạng thái — 4 khoá ĐÓNG theo `AI_PAGE_STATUSES`, vắng một khoá ⇒ 400 ở biên. */
export const adminAiRegistryByStatusSchema = z
  .object(
    Object.fromEntries(AI_PAGE_STATUSES.map((s) => [s, z.number().int().nonnegative()])) as Record<
      (typeof AI_PAGE_STATUSES)[number],
      z.ZodNumber
    >,
  )
  .strict();

/** Đếm trang theo tier — 4 khoá ĐÓNG theo `AI_PAGE_TIERS`. */
export const adminAiRegistryByTierSchema = z
  .object(
    Object.fromEntries(AI_PAGE_TIERS.map((t) => [t, z.number().int().nonnegative()])) as Record<
      (typeof AI_PAGE_TIERS)[number],
      z.ZodNumber
    >,
  )
  .strict();

/**
 * Response `GET /v1/admin/ai/registry`. KHÔNG phân trang: Sổ là hằng nạp lúc boot (vài chục trang),
 * không phải bảng DB — cursor ở đây sẽ là nghi lễ rỗng, và màn admin cần TOÀN BỘ để dựng ma trận
 * dẫn đường (một kết quả trỏ `next` tới trang ở "trang sau" thì ma trận thủng).
 */
export const adminAiRegistryResponseSchema = z
  .object({
    total: z.number().int().nonnegative(),
    byStatus: adminAiRegistryByStatusSchema,
    byTier: adminAiRegistryByTierSchema,
    pages: z.array(adminAiRegistryPageSchema),
  })
  .strict();

export type AdminAiRegistryResponse = z.infer<typeof adminAiRegistryResponseSchema>;
